import { defineStore } from 'pinia'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreTMDB } from '@/stores/storeTMDB'
import { PRECACHE_CONFIG, ITEM_TYPES } from '@/utils/preCacheConfig'

const log = (...args) => {
  if (PRECACHE_CONFIG.logEnabled) {
    console.log('[PreCache]', ...args)
  }
}

export const useStorePreCache = defineStore('storePreCache', {
  state: () => ({
    visibleItems: new Map(),
    queue: [],
    processing: false,
    isIdle: true,
    activeFetches: 0,
    paused: false,
    idleTimeout: null,
    batchTimeout: null,
    lastFetchTime: 0
  }),

  getters: {
    queueLength: (state) => state.queue.length,
    isProcessing: (state) => state.processing,
    isPaused: (state) => state.paused
  },

  actions: {
    /**
     * Register an item as visible in the viewport
     */
    registerVisible(item) {
      if (!PRECACHE_CONFIG.enabled || this.paused) return

      const key = `${item.type}:${item.id}`

      // Skip if already visible
      if (this.visibleItems.has(key)) return

      // Store visibility info
      this.visibleItems.set(key, {
        ...item,
        registeredAt: Date.now(),
        distanceFromCenter: item.distanceFromCenter || 0
      })

      // Add to queue if not already cached
      this._addToQueue(item)

      log(`Registered visible: ${key}`)
    },

    /**
     * Deregister an item when it leaves the viewport
     */
    deregisterVisible(item) {
      const key = `${item.type}:${item.id}`
      this.visibleItems.delete(key)
      log(`Deregistered: ${key}`)
    },

    /**
     * Record user activity (scroll, interaction)
     * Resets idle timer
     */
    recordActivity() {
      if (!PRECACHE_CONFIG.enabled) return

      this.isIdle = false

      // Clear existing idle timeout
      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout)
      }

      // Clear batch processing if active
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout)
        this.batchTimeout = null
      }

      // Set new idle timeout
      this.idleTimeout = setTimeout(() => {
        this.isIdle = true
        log('User is now idle')
        this._scheduleBatchProcessing()
      }, PRECACHE_CONFIG.idleThresholdMs)
    },

    /**
     * Pause pre-caching (during navigation)
     */
    pause() {
      this.paused = true
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout)
        this.batchTimeout = null
      }
      log('Pre-caching paused')
    },

    /**
     * Resume pre-caching
     */
    resume() {
      this.paused = false
      log(
        `Pre-caching resumed - isIdle: ${this.isIdle}, queueLength: ${this.queue.length}`
      )
      if (this.isIdle && this.queue.length > 0) {
        log('Triggering batch scheduling from resume')
        this._scheduleBatchProcessing()
      }
    },

    /**
     * Clear all state (on navigation)
     */
    clear() {
      this.visibleItems.clear()
      this.queue = []
      this.processing = false
      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout)
        this.idleTimeout = null
      }
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout)
        this.batchTimeout = null
      }
      log('Pre-cache state cleared')
    },

    /**
     * Add item to priority queue
     */
    _addToQueue(item) {
      const key = `${item.type}:${item.id}`

      // Check if type is configured and enabled
      const typeConfig = ITEM_TYPES[item.type]
      if (!typeConfig) {
        log(`Unknown item type: ${item.type}`)
        return
      }
      if (!typeConfig.enabled) {
        log(`Item type disabled: ${item.type}`)
        return
      }

      // Skip if already in queue
      if (this.queue.some((q) => q.key === key)) return

      // Skip if already cached
      if (this._isAlreadyCached(item)) {
        log(`Already cached: ${key}`)
        return
      }

      // Skip if queue is full
      if (this.queue.length >= PRECACHE_CONFIG.maxQueueSize) {
        log('Queue full, skipping')
        return
      }

      const basePriority = typeConfig.priority
      const centerBoost =
        item.distanceFromCenter < 0.3 ? PRECACHE_CONFIG.centerBias : 1.0
      const priority = basePriority * centerBoost

      this.queue.push({
        key,
        ...item,
        priority,
        addedAt: Date.now()
      })

      // Sort by priority (highest first), then by age (oldest first)
      this.queue.sort((a, b) => {
        if (b.priority !== a.priority) {
          return b.priority - a.priority
        }
        return a.addedAt - b.addedAt
      })

      log(
        `Added to queue: ${key} (priority: ${priority.toFixed(2)}, queue size: ${this.queue.length})`
      )

      // Try to schedule batch processing if conditions are right
      this._scheduleBatchProcessing()
    },

    /**
     * Check if item is already in the cache
     */
    _isAlreadyCached(item) {
      if (item.type === 'review') {
        const storeReviews = useStoreReviews()
        return !!storeReviews.getReviewById(item.id)
      }
      if (item.type === 'title') {
        const storeTMDB = useStoreTMDB()
        return !!storeTMDB.getTitleById(item.tmdbId, item.tmdbType)
      }
      // Future: handle list type
      return false
    },

    /**
     * Schedule batch processing during idle
     */
    _scheduleBatchProcessing() {
      log(
        `_scheduleBatchProcessing called - paused: ${this.paused}, isIdle: ${this.isIdle}, processing: ${this.processing}, queueLength: ${this.queue.length}, batchTimeout: ${!!this.batchTimeout}`
      )

      if (this.paused) {
        log('Skipping batch scheduling - paused')
        return
      }
      if (!this.isIdle) {
        log('Skipping batch scheduling - not idle')
        return
      }
      if (this.processing) {
        log('Skipping batch scheduling - already processing')
        return
      }
      if (this.queue.length === 0) {
        log('Skipping batch scheduling - queue empty')
        return
      }
      if (this.batchTimeout) {
        log('Skipping batch scheduling - already scheduled')
        return
      }

      log(`Scheduling batch in ${PRECACHE_CONFIG.batchIntervalMs}ms`)

      this.batchTimeout = setTimeout(() => {
        this.batchTimeout = null
        this._processBatch()
      }, PRECACHE_CONFIG.batchIntervalMs)
    },

    /**
     * Process a batch of queued items
     */
    async _processBatch() {
      log(
        `_processBatch called - paused: ${this.paused}, isIdle: ${this.isIdle}, processing: ${this.processing}`
      )

      if (this.paused) {
        log('Aborting batch - paused')
        return
      }
      if (!this.isIdle) {
        log('Aborting batch - not idle')
        return
      }
      if (this.processing) {
        log('Aborting batch - already processing')
        return
      }

      this.processing = true
      log(`Starting batch processing (${this.queue.length} items in queue)`)

      const batch = this.queue.splice(0, PRECACHE_CONFIG.batchSize)
      log(
        `Batch contains ${batch.length} items: ${batch.map((i) => i.key).join(', ')}`
      )

      for (const item of batch) {
        log(`Processing item: ${item.key}`)

        // Stop if no longer idle or paused
        if (!this.isIdle || this.paused) {
          log(`Stopping batch - isIdle: ${this.isIdle}, paused: ${this.paused}`)
          // Put unprocessed items back in queue
          this.queue.unshift(...batch.slice(batch.indexOf(item)))
          break
        }

        // Check concurrent fetch limit
        if (this.activeFetches >= PRECACHE_CONFIG.maxConcurrentFetches) {
          log(`Waiting for fetch slot (active: ${this.activeFetches})`)
          await this._waitForFetchSlot()
        }

        // Rate limiting
        const timeSinceLastFetch = Date.now() - this.lastFetchTime
        if (timeSinceLastFetch < PRECACHE_CONFIG.minFetchIntervalMs) {
          const waitTime =
            PRECACHE_CONFIG.minFetchIntervalMs - timeSinceLastFetch
          log(`Rate limiting - waiting ${waitTime}ms`)
          await this._delay(waitTime)
        }

        // Skip if now cached (might have been fetched by user action)
        if (this._isAlreadyCached(item)) {
          log(`Skipping ${item.key} - now cached`)
          continue
        }

        // Fetch the item
        log(`Fetching item: ${item.key}`)
        this._fetchItem(item)
      }

      this.processing = false

      // Schedule next batch if there are more items
      if (this.queue.length > 0 && this.isIdle && !this.paused) {
        this._scheduleBatchProcessing()
      }
    },

    /**
     * Fetch a single item
     */
    async _fetchItem(item) {
      this.activeFetches++
      this.lastFetchTime = Date.now()

      try {
        if (item.type === 'review') {
          const storeReviews = useStoreReviews()
          await storeReviews.fetchReviewById(item.id)
          log(`Fetched review: ${item.id}`)
        } else if (item.type === 'title') {
          const storeTMDB = useStoreTMDB()
          await storeTMDB.fetchTitleById(item.tmdbId, item.tmdbType)
          log(`Fetched title: ${item.tmdbType}/${item.tmdbId}`)
        }
        // Future: handle list type
      } catch (error) {
        // Silent failure - pre-cache errors are non-critical
        log(`Failed to fetch ${item.key}:`, error.message)
      } finally {
        this.activeFetches--
      }
    },

    /**
     * Wait for a fetch slot to become available
     */
    async _waitForFetchSlot() {
      while (this.activeFetches >= PRECACHE_CONFIG.maxConcurrentFetches) {
        await this._delay(50)
      }
    },

    /**
     * Promise-based delay
     */
    _delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    }
  }
})
